import { apiClient } from './apiClient';

import type { ApiResponse } from '@/types/auth.types';
import type {
  SubmissionDetail,
  SubmissionListData,
  SubmissionListParams,
  SubmissionStage,
} from '@/types/submission.types';

export async function getSubmissions(params: SubmissionListParams): Promise<SubmissionListData> {
  const searchParams = new URLSearchParams();

  if (params.q) searchParams.set('q', params.q);
  if (params.roles) searchParams.set('roles', params.roles);
  if (params.sites) searchParams.set('sites', params.sites);
  if (params.stages) searchParams.set('stages', params.stages);
  if (params.jobPostingId) searchParams.set('jobPostingId', params.jobPostingId);
  if (params.from) searchParams.set('from', params.from);
  if (params.to) searchParams.set('to', params.to);
  if (params.sortBy) searchParams.set('sortBy', params.sortBy);
  if (params.sortOrder) searchParams.set('sortOrder', params.sortOrder);
  if (params.page) searchParams.set('page', String(params.page));
  if (params.limit) searchParams.set('limit', String(params.limit));

  const query = searchParams.toString();
  const url = `/reviews/submissions${query ? `?${query}` : ''}`;

  const response = await apiClient.get<ApiResponse<SubmissionListData>>(url);
  return response.data;
}

export async function getSubmissionDetail(id: string): Promise<SubmissionDetail> {
  const response = await apiClient.get<ApiResponse<SubmissionDetail>>(`/reviews/submissions/${id}`);
  return response.data;
}

export const SUBMISSION_STAGES: SubmissionStage[] = [
  'submitted',
  'video_pending',
  'video_completed',
  'under_review',
  'approved',
  'rejected',
  'waitlist',
];

const HR_STORAGE_KEY = 'kent-hr-auth';
const API_BASE = '/api';

interface ExportFilters {
  q?: string;
  roles?: string[];
  sites?: string[];
  stages?: string[];
  from?: string;
  to?: string;
}

function getAccessToken(): string | null {
  try {
    const raw = localStorage.getItem(HR_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { state?: { accessToken?: string } };
    return parsed.state?.accessToken ?? null;
  } catch {
    return null;
  }
}

export async function exportSubmissionsCsv(filters: ExportFilters): Promise<void> {
  const params = new URLSearchParams();

  if (filters.q) params.set('q', filters.q);
  if (filters.roles && filters.roles.length > 0) params.set('roles', filters.roles.join(','));
  if (filters.sites && filters.sites.length > 0) params.set('sites', filters.sites.join(','));
  if (filters.stages && filters.stages.length > 0) params.set('stages', filters.stages.join(','));
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);

  const query = params.toString();
  const url = `${API_BASE}/reviews/submissions/export${query ? `?${query}` : ''}`;

  const token = getAccessToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, { headers });

  if (!response.ok) {
    throw new Error(`Export failed (${String(response.status)})`);
  }

  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition');
  let filename = `submissions-${new Date().toISOString().slice(0, 10)}.csv`;

  if (disposition) {
    const match = /filename="?([^";\n]+)"?/i.exec(disposition);
    if (match?.[1]) {
      filename = match[1];
    }
  }

  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(objectUrl);
}
